import { Injectable } from '@angular/core';
import { findIndex, isEmpty, isUndefined, last, sortBy } from 'lodash';
import { GlobalInfoService, Logger } from '../../core';
import { formMappings } from '../../forms';
import {
  Actividad,
  ActividadTiposEstado,
  Const,
  DynamicComponentForm,
  FormConfig,
  Microproceso,
  Tramite,
} from '../../shared';
import { FlowTitle } from '../title';


const log = new Logger('HelperFlowService');

interface ConfigRefineActivities {
  filterByStatus?: ActividadTiposEstado;
  sort: boolean;
}

export interface ConfigRefineTasks {
  filterByRootStatus?: boolean;
}

interface ParamsCbFlowHistory {
  idFlow: number;
}

// Modo en el que el flujo se comporta
export type FlowMode = 'normal' | 'completion' | 'association' | 'reports' | 'etapas';

@Injectable()
export class HelperFlowService {
  // Almacena los id de los flujos mostrados
  flowsHistory: number[] = [];
  // Contiene el flujo actual guardado
  flowHistoryActually: number[] = [];

  constructor(private gis: GlobalInfoService) { }

  /**
   * Retorna el estado de un flujo independientemente de que sea un trámite
   * por default, una instancia de un trámite o un microproceso.
   * Flujo actual es con el que se interactua, puede ser un trámite por default,
   * una instancia de un trámite o un microproceso.
   *
   * @param {(Tramite | Microproceso)} flow - Flujo actual, con el que se interactua.
   * @returns {string}
   * @memberof HelperFlowService
   */
  getFlowState(flow: Tramite | Microproceso): string {
    const key = this.flowsHistory.length > 1 ? 'tarea_estado' : 'tramite_estado';
    if (flow[key]) {
      return flow[key].nombre;
    } else if (key === 'tarea_estado' && flow.hasOwnProperty('finalizado')) {
      if (flow['finalizado']) {
        return Const.STATUS_TASK.finished;
      } else {
        return Const.STATUS_TASK.pending;
      }
    }
  }

  /**
   * Determina las clases css para personalizar el estado del flujo.
   *
   * @param {(Tramite | Microproceso)} flow
   * @returns
   * @memberof HelperFlowService
   */
  getClassFlowState(flow: Tramite | Microproceso) {
    // Color del estado asignado
    let colorStatus;
    if (flow['tramite_estado']) {
      if (flow['tramite_estado']['color']) {
        colorStatus = flow['tramite_estado']['color'];
      } else {
        colorStatus = '#868E96';
      }
    } else {
      if (flow['finalizado']) {
        colorStatus = '#28a745';
      } else {
        colorStatus = '#17a2b8';
      }
    }
    return {
      'background-color': colorStatus
    };
  }

  /**
   * Formatea las actividades de forma que sean entendibles
   * por el componente 'activity'.
   *
   * @param {Actividad[]} activities
   * @param {ConfigRefineActivities} [config={
   *       filterByState: 'inactive',
   *       sort: true
   *     }]
   * @returns
   * @memberof HelperFlowService
   */
  refineActivities(
    activities: Actividad[],
    config: Partial<ConfigRefineActivities> = {
      sort: true
    }
  ) {
    if (!isEmpty(config.filterByStatus)) {
      // Posibles estados de la actividad
      const states = Const.STATUS_ACTIVITY;
      // Actividades filtradas por estado
      if (activities && activities.length) {
        activities = activities.filter(a => {
          return a.actividad_estado.nombre !== states[config.filterByStatus];
        });
      }
    }
    if (activities && activities.length && config.sort) {
      // Ordenar actividades en forma asc en base a su key "orden"
      activities = sortBy(activities, 'orden');
    }
    if (activities && activities.length) {
      // Agregar metadatos a las actividades
      let indexFlow = 1;
      activities.map((item, i) => {
        item['meta'] = {
          index: i,
          indexLast: activities.length - 1,
          isLast: activities.length - 1 === i,
          // Numerar las actividades visibles con un identificador.
          indexFlow: indexFlow
        };
        indexFlow++;
      });
    }
    return activities;
  }

  /**
   * Retorna un componente que representa un formulario
   * que sera usado en un tipo de tarea.
   *
   * @param {string} type
   * @param {string} name
   * @returns
   */
  getFormType(type: string, name: string = 'index', specific_form: boolean = true): FormConfig<DynamicComponentForm> {
    let form: FormConfig<DynamicComponentForm> = null;
    if (specific_form) {
      if (formMappings.hasOwnProperty(type) && formMappings[type].hasOwnProperty(name)) {
        form = formMappings[type][name];
      } else {
        log.warn(
          `Posible error introducido por el desarrollador: se esta intentando obtener
           una forma de tipo '${type}' con el nombre '${name}' la cual no esta configurada en 'forms-mappings'.`
        );
      }
    } else {
      if (formMappings.hasOwnProperty(type) && formMappings[type].hasOwnProperty('dynamic_form')) {
        form = formMappings[type]['dynamic_form'];
      } else {
        log.warn(
          `Posible error introducido por el desarrollador: se esta intentando obtener
           una forma de tipo '${type}' con el nombre '${name}' la cual no esta configurada en 'forms-mappings'.`
        );
      }
    }
    return form;
  }

  /**
   * Formatea las tareas de forma que sean entendibles
   * por el componente 'task'.
   *
   * @param {Tarea[]} tasks
   * @param {Partial<ConfigRefineTasks>} [config={
   *       sort: true
   *     }]
   * @returns
   * @memberof ActivityComponent
   */
  refineTasks(activity: Actividad, config: Partial<ConfigRefineTasks> = {}) {
    let tasks = activity.tareas;
    // Ordenar actividades en forma asc en base a su key "orden"
    tasks = sortBy(tasks, 'orden');
    // Actividades filtradas por estado
    function filterByRootStatus(status: boolean) {
      return tasks.filter(t => {
        return t.activo === status;
      });
    }
    // Filtro definido por el componente
    if (!isUndefined(config.filterByRootStatus)) {
      tasks = filterByRootStatus(config.filterByRootStatus);
    }
    // Última tarea activa
    const lastTaskActive = last(filterByRootStatus(true));
    // Indice de la ultima tarea
    let indexLastTaskActive = -1;
    if (lastTaskActive) {
      indexLastTaskActive = findIndex(tasks, { id: lastTaskActive.id });
    }
    // Agregar metadatos a las tareas
    let indexFlow = 1;
    tasks.map((item, i) => {
      const type = item.tarea_tipo.nombre;
      const name = type === Const.TYPES_TASK.form ?
                    item ?
                      item.tarea_forma ?
                        item.tarea_forma.length ?
                          item.tarea_forma[0].nombre
                        : undefined
                      : undefined
                    : undefined
                   : undefined;
      // Obtener el formulario para renderizar la tarea
      let formType = null;
      if (type !== Const.TYPES_TASK.microProcess) {
        const forma: Array<any> = new Array(item.tarea_forma);
        if (forma[0].length > 0) {
          formType = this.getFormType(type, name, item.tarea_forma[0].forma_especifica);
        } else {
          formType = this.getFormType(type, name, true);
        }
      }
      // Configurar metadatos de la tarea
      item['meta'] = {
        idActivity: activity.id,
        index: i,
        indexLast: tasks.length - 1,
        isLast: tasks.length - 1 === i,
        lastIndexActive: indexLastTaskActive,
        isLastActive: indexLastTaskActive === i,
        belongsToLastActivity: activity.meta.isLast,
        formType: formType
      };
      // Numerar las tareas activas con un identificador,
      // el cual será mostrado al usuario.
      if (item.activo) {
        item.meta.indexFlow = indexFlow;
        indexFlow++;
      }
    });
    return tasks;
  }

  /**
   * Almacena o elimina(todos) los ids de las instancias de los
   * diferentes niveles del flujo en el historial,
   * dependiendo si se esta entrando o saliendo de un nivel.
   * Nota los niveles son la profundidad del flujo:
   * - nivel 1 => Instancia de un trámite por default.
   * - nivel 2..N => Microproceso.
   *
   * @param {(Tramite | Microproceso)} flow
   * @param {boolean} [goBack=false] - Define si se esta ascendiendo de un
   * @param {boolean} [clean=false] - Resetea el historial de flujo, y define el nivel 1.
   * @param {boolean} [changeCurrent=false] - Cambia el historial actual por la nueva consulta
   * @memberof HelperFlowService
   */
  updateFlowHistory(
    flow: Tramite | Microproceso | number,
    goBack: boolean = false,
    clean: boolean = false,
    changeCurrent: boolean = false
  ) {
    const idFlow = typeof flow === 'number' ? flow : flow.id;
    if (clean) {
      this.flowsHistory = [];
    }
    if (!goBack) {
      if (!changeCurrent) {
        this.flowsHistory.push(idFlow);
      } else {
        this.flowsHistory[this.flowsHistory.length - 1] = idFlow;
      }
    }
    if (typeof flow !== 'number') {
      this.gis.set({ flow: flow, flowsHistory: this.flowsHistory });
    }
    log.debug('flow:', idFlow);
    log.debug('flow history: ', this.flowsHistory);
  }

  /**
   * Guarda el flujo actual
   *
   * @memberof HelperFlowService
   */
  saveFlow() {
    this.flowHistoryActually = this.flowsHistory;
  }

  /**
   * Retrocede un nivel en el flujo.
   *
   * @param {(...arg: any[]) => void} runTramite
   * @param {(...arg: any[]) => void} runMicroProcess
   * @param {number} [idFlow=0]
   * @param {boolean} [saveFlowHistoryActually=false]
   * @memberof HelperFlowService
   */
  returnToPreviousFlow(
    runTramite: (...arg: any[]) => void,
    runMicroProcess: (...arg: any[]) => void,
    idFlow: number = 0,
    saveFlowHistoryActually: boolean = false
  ) {
    // Obtener id del flujo anterior
    let idFlowPrevius = 0;
    if (idFlow === 0) {
      // Eliminar id del flujo actual
      const fhm = this.flowsHistory.slice(0, this.flowsHistory.length - 1);
      this.flowsHistory = fhm;
      idFlowPrevius = this.flowsHistory.slice(-1)[0];
    } else {
      if (saveFlowHistoryActually) {
        this.flowsHistory = this.flowHistoryActually;
      }
      while (this.flowsHistory.slice(-1)[0] !== idFlow && this.flowsHistory.length > 0) {
        const fhm = this.flowsHistory.slice(0, this.flowsHistory.length - 1);
        this.flowsHistory = fhm;
      }
      idFlowPrevius = this.flowsHistory.slice(-1)[0];
    }
    // Si el flujo anterior no es el primer elemento del historial
    if (this.flowsHistory.lastIndexOf(idFlowPrevius) !== 0) {
      // Generar el flujo con un microproceso
      runMicroProcess(idFlowPrevius);
    } else {
      // Generar el flujo con un trámite ya se uno por default o una instancia
      runTramite(idFlowPrevius);
    }
  }

  /**
   * Recarga el flujo actual.
   *
   * @param {(...arg: any[]) => void} runTramite
   * @param {(...arg: any[]) => void} runMicroProcess
   * @memberof HelperFlowService
   */
  reloadFlow(runTramite: (...arg: any[]) => void, runMicroProcess: (...arg: any[]) => void) {
    // Instancia actual
    const currentFlow = this.flowsHistory.slice(-1)[0];
    // Eliminar la instancia actual
    this.flowsHistory.pop();
    // Si no hay ninguna instancia en el historial
    if (!this.flowsHistory.length) {
      runTramite(currentFlow);
    } else {
      runMicroProcess(currentFlow, false);
    }
  }

  /**
   * Extrae la data necesaria para generar el título del flujo.
   *
   * @param {(Tramite | Microproceso)} flow
   * @returns {FlowTitle}
   * @memberof HelperFlowService
   */
  getFlowTitle(flow: Tramite | Microproceso): FlowTitle | null {
    let data: FlowTitle = null;
    if (flow) {
      data = {
        id: flow.id,
        name: flow['nombre'] ? flow['nombre'] : flow['descripcion'],
        state: this.getFlowState(flow),
        classState: this.getClassFlowState(flow),
        lengthFlowsHistory: this.flowsHistory.length,
        notSynchronized: false,
        microProcess: flow['tramite_estado'] ? false : true
      };

      if (flow['expediente']) {
        data.expediente = flow['expediente']['codigo'];
        data.idExpediente = flow['expediente']['id'];
        data.estado_expediente = flow['expediente']['expediente_estado']['nombre'];
        data.solicitud_radicado = flow['expediente']['radicado_solicitud'];
        data.nombreTramite = flow['expediente']['nombre_tramite'] ? flow['expediente']['nombre_tramite']: "";
        if (flow['expediente']['id_tramite']) {
          data.idTramite = flow['expediente']['id_tramite'];
          data.liquidation = flow['liquidacion'];
          data.idLiquidation = flow['codigo_liquidacion'];
        } else {
          data.idTramite = flow.id;
          data.liquidation = flow['liquidacion'];
          data.idLiquidation = flow['codigo_liquidacion'];
        }
      }

    }
    return data;
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/shared/services/helper-flow.service.ts